import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Plus, Eye, Edit, Trash2, TrendingUp, Package, Calendar, Users } from 'lucide-react';
import { batchService, Batch } from '../services/batchService';
import { designTokens } from '../design/designTokens';

// Type definitions for Cobb 500 standards
type WeightStandard = { age: number; weight: number; description: string };
type FeedStandard = { age: number; cumulative: number; daily: number };
type FcrStandard = { age: number; fcr: number };

// Cobb 500 Broiler Performance Standards (based on the PDF you referenced)
const COBB_500_STANDARDS = {
  weight: [
    { age: 0, weight: 0.042, description: 'Day 0' },
    { age: 7, weight: 0.165, description: 'Week 1' },
    { age: 14, weight: 0.420, description: 'Week 2' },
    { age: 21, weight: 0.820, description: 'Week 3' },
    { age: 28, weight: 1.350, description: 'Week 4' },
    { age: 35, weight: 1.950, description: 'Week 5' },
    { age: 42, weight: 2.550, description: 'Week 6' },
    { age: 49, weight: 3.100, description: 'Week 7' },
  ] as WeightStandard[],
  feed: [
    { age: 0, cumulative: 0, daily: 0 },
    { age: 7, cumulative: 0.120, daily: 0.017 },
    { age: 14, cumulative: 0.350, daily: 0.033 },
    { age: 21, cumulative: 0.720, daily: 0.053 },
    { age: 28, cumulative: 1.250, daily: 0.076 },
    { age: 35, cumulative: 1.900, daily: 0.093 },
    { age: 42, cumulative: 2.700, daily: 0.114 },
    { age: 49, cumulative: 3.550, daily: 0.121 },
  ] as FeedStandard[],
  fcr: [
    { age: 0, fcr: 0 },
    { age: 7, fcr: 0.73 },
    { age: 14, fcr: 0.83 },
    { age: 21, fcr: 0.88 },
    { age: 28, fcr: 0.93 },
    { age: 35, fcr: 0.97 },
    { age: 42, fcr: 1.06 },
    { age: 49, fcr: 1.15 },
  ] as FcrStandard[]
};

// Batch interface is now imported from batchService

const Container = styled.div`
  padding: ${designTokens.spacing[6]};
  max-width: 1200px;
  margin: 0 auto;
  font-family: ${designTokens.typography.fontFamily.primary};
  background: ${designTokens.colors.background.primary};
  min-height: 100vh;
  color: ${designTokens.colors.text.primary};

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[4]};
  }
`;


const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${designTokens.spacing[6]};
  margin-bottom: ${designTokens.spacing[8]};

  ${designTokens.mediaQueries.maxMd} {
    grid-template-columns: 1fr;
    gap: ${designTokens.mobileSpacing[4]};
  }
`;

const StatCard = styled.div`
  background: ${designTokens.colors.background.secondary};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing[6]};
  box-shadow: ${designTokens.shadows.card};
  border: 1px solid ${designTokens.colors.border.primary};
  transition: ${designTokens.transitions.default};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${designTokens.shadows.cardHover};
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[3]};
  margin-bottom: ${designTokens.spacing[4]};
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${designTokens.borderRadius.md};
  background: ${props => props.color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color};
`;

const StatTitle = styled.h3`
  font-size: ${designTokens.typography.fontSize.base};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  color: ${designTokens.colors.text.primary};
  margin: 0;

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const StatValue = styled.div`
  font-size: ${designTokens.typography.fontSize['2xl']};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.riverPoultry.brandColors.primary};
  margin-bottom: ${designTokens.spacing[2]};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xl};
  }
`;

const StatSubtext = styled.div`
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.muted};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xs};
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designTokens.spacing[8]};
  flex-wrap: wrap;
  gap: ${designTokens.spacing[4]};

  ${designTokens.mediaQueries.maxMd} {
    flex-direction: column;
    align-items: stretch;
    gap: ${designTokens.mobileSpacing[3]};
  }
`;

const AddButton = styled.button`
  background: ${designTokens.riverPoultry.gradients.primary};
  color: ${designTokens.colors.text.inverse};
  border: none;
  border-radius: ${designTokens.borderRadius.full};
  padding: ${designTokens.spacing[4]} ${designTokens.spacing[7]};
  font-size: ${designTokens.typography.fontSize.base};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[2]};
  transition: ${designTokens.transitions.default};
  font-family: ${designTokens.typography.fontFamily.primary};
  box-shadow: ${designTokens.shadows.button};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${designTokens.shadows.buttonHover};
  }

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[3]} ${designTokens.mobileSpacing[6]};
    font-size: ${designTokens.mobileTypography.fontSize.sm};
  }
`;

const BatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${designTokens.spacing[6]};

  ${designTokens.mediaQueries.maxMd} {
    grid-template-columns: 1fr;
    gap: ${designTokens.mobileSpacing[4]};
  }
`;

const BatchCard = styled.div<{ status: string }>`
  background: ${designTokens.colors.background.secondary};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing[6]};
  box-shadow: ${designTokens.shadows.card};
  border: 1px solid ${designTokens.colors.border.primary};
  border-left: 4px solid ${props => 
    props.status === 'active' ? designTokens.colors.status.success : 
    props.status === 'completed' ? designTokens.colors.status.info : designTokens.colors.status.error
  };
  transition: ${designTokens.transitions.default};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${designTokens.shadows.cardHover};
  }
`;

const BatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${designTokens.spacing[4]};
`;

const BatchTitle = styled.h3`
  font-size: ${designTokens.typography.fontSize.xl};
  font-weight: ${designTokens.typography.fontWeight.bold};
  color: ${designTokens.colors.text.primary};
  margin: 0;

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.lg};
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: ${designTokens.spacing[1]} ${designTokens.spacing[3]};
  border-radius: ${designTokens.borderRadius.full};
  font-size: ${designTokens.typography.fontSize.xs};
  font-weight: ${designTokens.typography.fontWeight.semibold};
  text-transform: uppercase;
  background: ${props => 
    props.status === 'active' ? designTokens.colors.status.successLight : 
    props.status === 'completed' ? designTokens.colors.status.infoLight : designTokens.colors.status.errorLight
  };
  color: ${props => 
    props.status === 'active' ? designTokens.colors.status.success : 
    props.status === 'completed' ? designTokens.colors.status.info : designTokens.colors.status.error
  };

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xs};
    padding: ${designTokens.mobileSpacing[1]} ${designTokens.mobileSpacing[2]};
  }
`;

const BatchInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${designTokens.spacing[4]};
  margin-bottom: ${designTokens.spacing[6]};

  ${designTokens.mediaQueries.maxMd} {
    grid-template-columns: 1fr;
    gap: ${designTokens.mobileSpacing[3]};
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[2]};
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.muted};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xs};
  }
`;

const ProgressSection = styled.div`
  margin-bottom: ${designTokens.spacing[6]};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${designTokens.colors.background.tertiary};
  border-radius: ${designTokens.borderRadius.sm};
  overflow: hidden;
  margin-bottom: ${designTokens.spacing[2]};
`;

const ProgressFill = styled.div<{ percentage: number; color: string }>`
  width: ${props => props.percentage}%;
  height: 100%;
  background: ${props => props.color};
  transition: ${designTokens.transitions.default};
`;

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.muted};

  ${designTokens.mediaQueries.maxMd} {
    font-size: ${designTokens.mobileTypography.fontSize.xs};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${designTokens.spacing[2]};

  ${designTokens.mediaQueries.maxMd} {
    flex-direction: column;
    gap: ${designTokens.mobileSpacing[2]};
  }
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'danger' }>`
  padding: ${designTokens.spacing[2]} ${designTokens.spacing[4]};
  border-radius: ${designTokens.borderRadius.md};
  border: none;
  font-size: ${designTokens.typography.fontSize.sm};
  font-weight: ${designTokens.typography.fontWeight.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing[2]};
  transition: ${designTokens.transitions.default};
  font-family: ${designTokens.typography.fontFamily.primary};

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${designTokens.riverPoultry.brandColors.primary};
          color: ${designTokens.colors.text.inverse};
          &:hover { background: ${designTokens.riverPoultry.brandColors.primaryDark}; }
        `;
      case 'secondary':
        return `
          background: ${designTokens.colors.background.tertiary};
          color: ${designTokens.colors.text.muted};
          &:hover { background: ${designTokens.colors.background.hover}; }
        `;
      case 'danger':
        return `
          background: ${designTokens.colors.status.errorLight};
          color: ${designTokens.colors.status.error};
          &:hover { background: ${designTokens.colors.status.errorLight}; }
        `;
    }
  }}

  ${designTokens.mediaQueries.maxMd} {
    padding: ${designTokens.mobileSpacing[2]} ${designTokens.mobileSpacing[3]};
    font-size: ${designTokens.mobileTypography.fontSize.xs};
  }
`;

const Modal = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: #f5f5f5;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2e7d32;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2e7d32;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2e7d32;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 14px 28px;
  border-radius: 25px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: #2e7d32;
          color: white;
          &:hover { background: #1b5e20; }
        `;
      case 'secondary':
        return `
          background: #f5f5f5;
          color: #666;
          &:hover { background: #e0e0e0; }
        `;
    }
  }}
`;

const BatchManagement: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [batches, setBatches] = useState<Batch[]>([]);

  // Load batches from the shared service on component mount
  useEffect(() => {
    const loadedBatches = batchService.loadBatches();
    setBatches(loadedBatches);
  }, []);

  // Check for URL parameter to open form automatically
  useEffect(() => {
    const openForm = searchParams.get('openForm');
    if (openForm === 'true') {
      setModalOpen(true);
      // Clear the URL parameter after opening the form
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openForm');
      window.history.replaceState({}, '', `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`);
    }
  }, [searchParams]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    breed: 'Cobb 500',
    initialBirds: '',
    notes: ''
  });

  const getCobbStandard = (age: number, type: 'weight' | 'feed' | 'fcr') => {
    if (type === 'weight') {
      const standard = COBB_500_STANDARDS.weight.find(s => s.age === age);
      return standard?.weight || 0;
    } else if (type === 'feed') {
      const standard = COBB_500_STANDARDS.feed.find(s => s.age === age);
      return standard?.cumulative || 0;
    } else {
      const standard = COBB_500_STANDARDS.fcr.find(s => s.age === age);
      return standard?.fcr || 0;
    }
  };

  const getPerformanceStatus = (current: number, target: number) => {
    const percentage = (current / target) * 100;
    if (percentage >= 105) return { status: 'excellent', color: '#4caf50' };
    if (percentage >= 95) return { status: 'good', color: '#8bc34a' };
    if (percentage >= 85) return { status: 'fair', color: '#ff9800' };
    return { status: 'poor', color: '#f44336' };
  };

  // Enhanced data validation
  const validateBatchForm = (): string[] => {
    const errors: string[] = [];
    
    if (!formData.name.trim()) errors.push('Batch name is required');
    if (!formData.startDate) errors.push('Start date is required');
    if (!formData.breed) errors.push('Breed selection is required');
    if (!formData.initialBirds || parseInt(formData.initialBirds) <= 0) {
      errors.push('Valid initial bird count is required');
    }
    
    // Check for duplicate batch names
    const existingBatch = batches.find(batch => 
      batch.name.toLowerCase() === formData.name.toLowerCase() && 
      batch.id !== editingBatch?.id
    );
    if (existingBatch) {
      errors.push('A batch with this name already exists');
    }
    
    // Validate start date (not in the future)
    const startDate = new Date(formData.startDate);
    const today = new Date();
    if (startDate > today) {
      errors.push('Start date cannot be in the future');
    }
    
    // Validate initial bird count (reasonable range)
    const birdCount = parseInt(formData.initialBirds);
    if (birdCount < 10 || birdCount > 10000) {
      errors.push('Initial bird count should be between 10 and 10,000');
    }
    
    return errors;
  };


  const totalBirds = batches.reduce((sum, batch) => sum + batch.currentBirds, 0);
  const activeBatches = batches.filter(batch => batch.status === 'active').length;
  const avgWeight = batches.length > 0 ? 
    batches.reduce((sum, batch) => sum + (batch.currentWeight || 0), 0) / batches.length : 0;
  const totalMortality = batches.reduce((sum, batch) => sum + (batch.mortality || 0), 0);

  const handleOpenModal = (batch?: Batch) => {
    if (batch) {
      setEditingBatch(batch);
      setFormData({
        name: batch.name,
        startDate: batch.startDate,
        breed: batch.breed,
        initialBirds: (batch.initialBirds || batch.totalBirds).toString(),
        notes: batch.notes || ''
      });
    } else {
      setEditingBatch(null);
      setFormData({
        name: '',
        startDate: '',
        breed: 'Cobb 500',
        initialBirds: '',
        notes: ''
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingBatch(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form data
    const errors = validateBatchForm();
    if (errors.length > 0) {
      // Validation errors will be shown inline
      return;
    }
    
    if (editingBatch) {
      // Update existing batch using the shared service
      const updatedBatch = batchService.updateBatch(editingBatch.id, {
        ...formData,
        initialBirds: parseInt(formData.initialBirds),
        currentBirds: parseInt(formData.initialBirds),
        totalBirds: parseInt(formData.initialBirds)
      });
      
      if (updatedBatch) {
        // Reload batches from the service
        const loadedBatches = batchService.loadBatches();
        setBatches(loadedBatches);
        // Batch updated successfully
      }
    } else {
      // Add new batch using the shared service
      batchService.addBatch({
        name: formData.name,
        startDate: formData.startDate,
        breed: formData.breed,
        age: 0,
        status: 'active',
        expectedEndDate: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString(),
        totalBirds: parseInt(formData.initialBirds),
        currentBirds: parseInt(formData.initialBirds),
        mortalityRate: 0,
        feedConsumption: 0,
        weight: 0,
        location: 'House 1',
        notes: formData.notes,
        initialBirds: parseInt(formData.initialBirds),
        currentWeight: 0,
        targetWeight: 2.5,
        mortality: 0
      });
      
      // Reload batches from the service
      const loadedBatches = batchService.loadBatches();
      setBatches(loadedBatches);
      // Batch created successfully
    }
    
    handleCloseModal();
  };

  const handleDelete = (id: string) => {
    // Delete batch using the shared service
    const success = batchService.deleteBatch(id);
    
    if (success) {
      // Reload batches from the service
      const loadedBatches = batchService.loadBatches();
      setBatches(loadedBatches);
    }
  };

  const handleClearAllData = () => {
    if (window.confirm('Are you sure you want to clear all batch data? This action cannot be undone.')) {
      // Clear all batches from localStorage
      batchService.clearAllBatches();
      // Update the state to reflect the empty data
      setBatches([]);
    }
  };

  return (
    <Container>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatIcon color="#2E7D32">
              <Users size={24} />
            </StatIcon>
            <StatTitle>Total Birds</StatTitle>
          </StatHeader>
          <StatValue>{totalBirds.toLocaleString()}</StatValue>
          <StatSubtext>Across all batches</StatSubtext>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#2E7D32">
              <Package size={24} />
            </StatIcon>
            <StatTitle>Active Batches</StatTitle>
          </StatHeader>
          <StatValue>{activeBatches}</StatValue>
          <StatSubtext>Currently running</StatSubtext>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#FF8C00">
              <TrendingUp size={24} />
            </StatIcon>
            <StatTitle>Average Weight</StatTitle>
          </StatHeader>
          <StatValue>{avgWeight.toFixed(2)} kg</StatValue>
          <StatSubtext>Current average</StatSubtext>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatIcon color="#f44336">
              <Calendar size={24} />
            </StatIcon>
            <StatTitle>Total Mortality</StatTitle>
          </StatHeader>
          <StatValue>{totalMortality}</StatValue>
          <StatSubtext>Birds lost</StatSubtext>
        </StatCard>
      </StatsGrid>

      <ActionBar>
        <AddButton onClick={() => handleOpenModal()}>
          <Plus size={20} />
          Add New Batch
        </AddButton>
        <ActionButton variant="danger" onClick={handleClearAllData}>
          <Trash2 size={16} />
          Clear All Data
        </ActionButton>
      </ActionBar>

      <BatchGrid>
        {batches.map(batch => {
          const targetWeight = getCobbStandard(batch.age, 'weight');
          const performance = getPerformanceStatus(batch.currentWeight || 0, targetWeight);
          const weightProgress = targetWeight > 0 ? ((batch.currentWeight || 0) / targetWeight) * 100 : 0;
          
          return (
            <BatchCard key={batch.id} status={batch.status}>
              <BatchHeader>
                <BatchTitle>{batch.name}</BatchTitle>
                <StatusBadge status={batch.status}>{batch.status}</StatusBadge>
              </BatchHeader>

              <BatchInfo>
                <InfoItem>
                  <Calendar size={16} />
                  <span>Age: {batch.age} days</span>
                </InfoItem>
                <InfoItem>
                  <Users size={16} />
                  <span>{batch.currentBirds} birds</span>
                </InfoItem>
                <InfoItem>
                  <TrendingUp size={16} />
                  <span>{batch.currentWeight || 0} kg</span>
                </InfoItem>
                <InfoItem>
                  <Package size={16} />
                  <span>{batch.feedConsumption} kg</span>
                </InfoItem>
              </BatchInfo>

              <ProgressSection>
                <ProgressText>
                  <span>Weight Progress</span>
                  <span>{performance.status.toUpperCase()}</span>
                </ProgressText>
                <ProgressBar>
                  <ProgressFill 
                    percentage={Math.min(weightProgress, 100)} 
                    color={performance.color}
                  />
                </ProgressBar>
                <ProgressText>
                  <span>Target: {targetWeight} kg</span>
                  <span>{weightProgress.toFixed(1)}%</span>
                </ProgressText>
              </ProgressSection>

              <ActionButtons>
                <ActionButton variant="primary" onClick={() => handleOpenModal(batch)}>
                  <Eye size={16} />
                  View
                </ActionButton>
                <ActionButton variant="secondary" onClick={() => handleOpenModal(batch)}>
                  <Edit size={16} />
                  Edit
                </ActionButton>
                <ActionButton variant="danger" onClick={() => handleDelete(batch.id)}>
                  <Trash2 size={16} />
                  Delete
                </ActionButton>
              </ActionButtons>
            </BatchCard>
          );
        })}
      </BatchGrid>

      <Modal open={modalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingBatch ? 'Edit Batch' : 'Add New Batch'}
            </ModalTitle>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Batch Name</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Breed</Label>
              <Select
                value={formData.breed}
                onChange={(e) => setFormData({ ...formData, breed: e.target.value })}
              >
                <option value="Cobb 500">Cobb 500</option>
                <option value="Ross 308">Ross 308</option>
                <option value="Hubbard">Hubbard</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Initial Number of Birds</Label>
              <Input
                type="number"
                value={formData.initialBirds}
                onChange={(e) => setFormData({ ...formData, initialBirds: e.target.value })}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Notes</Label>
              <TextArea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Add any notes about this batch..."
              />
            </FormGroup>

            <ModalActions>
              <Button type="button" variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {editingBatch ? 'Update' : 'Create'} Batch
              </Button>
            </ModalActions>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default BatchManagement;
